<template>
  <div class="chat-widget">
    <!-- Chat Overlay -->
    <ChatOverlay :is-visible="isChatOverlayVisible" :interaction-height="interactionHeight" @close="closeChatOverlay" />

    <!-- Chat Interaction -->
    <div ref="interactionWrapper" class="chat-input-wrapper" :style="inputWrapperStyle" @click="openChatOverlay">
      <ChatInteraction :is-opened="isChatOverlayVisible" />
    </div>

    <!-- Error Dialog -->
    <PlsDialog v-if="errorDialog?.open" v-bind="errorDialog" />

    <!-- Voice permission / disclaimer (store wizard). Omit on conversation-details — that route uses a local wizard + PlsDialog -->
    <PlsDialog v-if="showVoiceChatDialogFromStore" v-bind="voiceChatDialogFromStore" />

    <WidgetThresholdLimitModal
      v-if="thresholdLimitModal?.open"
      v-bind="thresholdLimitModal"
      @back="handleThresholdLimitBack"
      @open-request="handleThresholdLimitOpenRequest"
      @live-chat="handleThresholdLimitLiveChat"
      @call="handleThresholdLimitCall"
    />

    <!-- Trigger Dialog -->
    <PlsDialog v-if="triggerDialog?.open" v-bind="triggerDialog" />

    <!-- Open Request Modal -->
    <OpenRequestModal
      v-if="showOpenRequestDialog && currentTrigger"
      :trigger="currentTrigger"
      :selected-options="selectedOptions"
      @end-chat="handleEndChat"
      @restart-chat="handleRestartChat"
      @go-to-my-request="handleGoToMyRequest"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, unref, onMounted, onBeforeUnmount } from 'vue';
import ChatInteraction from './components/ChatInteraction.vue';
import ChatOverlay from './components/ChatOverlay.vue';
import PlsDialog from '@/components/PlsDialog.vue';
import WidgetThresholdLimitModal from './components/WidgetThresholdLimitModal.vue';
import { ConnectOptionType } from '@/common/bot/enum/ConnectOptionType';
import { useChatWidget } from '@/stores/useChatWidget';
import { storeToRefs } from 'pinia';
import { useOverlayCustomisation } from './composables/useOverlayCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { useDialogTrigger } from '@/stores/useDialogTrigger';
import { OpenRequestModal } from '@/components/OpenRequestModal';
import { useRouter } from 'vue-router/composables';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { ExternalBotEvent } from '@/common/bot/enum';
import { randomId } from '@/common/bot/lib';
import { useOpenChatsQuery } from '@/library/cf-v1/useOpenChatsQuery';

// State
const chatWidget = useChatWidget();
const { setIsChatOverlayVisible } = chatWidget;
const { isChatOverlayVisible } = storeToRefs(chatWidget);

const widgetConversation = useWidgetConversation();
const { endChat: widgetConversationEndChat, submitFlowExternalAction, openLandingOverlay } = widgetConversation;
const { supportWizard } = storeToRefs(widgetConversation);
const errorDialog = computed(() => unref(supportWizard.value.errorDialog));

const showVoiceChatDialogFromStore = computed(() => voiceChatDialogFromStore.value?.open);
const voiceChatDialogFromStore = computed(() => unref(supportWizard.value.support.voice.voiceChatDialog));
const thresholdLimitModal = computed(() => unref(supportWizard.value.thresholdLimitModal));

const connectConfigStore = useConnectConfig();
const { connectConfig } = storeToRefs(connectConfigStore);
const customisation = useOverlayCustomisation();
const router = useRouter();

// Open Chats Query (for checking loading state)
const { isLoading: isOpenChatsLoading } = useOpenChatsQuery();

// Dialog Trigger Store
const dialogTriggerStore = useDialogTrigger();
const { triggerDialog, currentTrigger, showOpenRequestDialog } = storeToRefs(dialogTriggerStore);
const { onTriggerMatch, setupTriggerDialog, closeOpenRequestDialog } = dialogTriggerStore;

// Setup trigger match handler
onTriggerMatch((trigger) =>
  setupTriggerDialog(trigger, async () => {
    await widgetConversationEndChat();
  })
);

// Refs
const interactionWrapper = ref<HTMLDivElement | null>(null);
const interactionHeight = ref(0);

// Plain let (not ref) - only used imperatively in lifecycle hooks, not reactive data
let resizeObserver: ResizeObserver | null = null;

const inputWrapperStyle = computed(() => SUtils.createBackgroundStyle(customisation.backgroundColor));

// TODO: take from connectConfig
const selectedOptions = computed(() => {
  return {
    category: connectConfig.value?.category ?? undefined,
    section: connectConfig.value?.section ?? undefined,
    article: undefined,
  };
});

// Trigger dialog props for PlsDialog
const triggerDialogProps = computed(() => {
  if (!triggerDialog.value) return null;

  return {
    open: true,
    hasSlot: false,
    title: '',
    content: triggerDialog.value.content,
    buttons: triggerDialog.value.actions.map((action) => ({
      name: action.text,
      callback: action.action,
      class: action.color ? [action.color] : [],
    })),
  };
});

// Calculate interaction height
const updateInteractionHeight = () => {
  nextTick(() => {
    if (interactionWrapper.value) {
      interactionHeight.value = interactionWrapper.value.offsetHeight;
    }
  });
};

// Setup ResizeObserver to watch for any height changes in ChatInteraction
onMounted(() => {
  if (interactionWrapper.value) {
    resizeObserver = new ResizeObserver(() => {
      updateInteractionHeight();
    });
    resizeObserver.observe(interactionWrapper.value);
  }
});

// Cleanup ResizeObserver on unmount
onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// Methods
const openChatOverlay = () => {
  // Don't open overlay if open chats are still being fetched
  if (isOpenChatsLoading.value || isChatOverlayVisible.value) {
    return;
  }

  openLandingOverlay();
};

const closeChatOverlay = () => {
  setIsChatOverlayVisible(false);
};

// OpenRequestModal handlers
const handleEndChat = async () => {
  await widgetConversationEndChat();
  closeOpenRequestDialog();
};

const handleRestartChat = () => {
  router.push('/new-home');
  submitFlowExternalAction({ type: ExternalBotEvent.RESTART_CHAT, id: randomId() });
  closeOpenRequestDialog();
};

const handleGoToMyRequest = () => {
  setIsChatOverlayVisible(false);
  router.push('/conversations');
  closeOpenRequestDialog();
};

const handleThresholdLimitBack = () => {
  widgetConversation.supportWizard.closeThresholdLimitModal();
};

const handleThresholdLimitOpenRequest = () => {
  widgetConversation.supportWizard.submitSelectedConnectOption({ connectOption: ConnectOptionType.OPEN_REQUEST });
  widgetConversation.supportWizard.closeThresholdLimitModal();
};

const handleThresholdLimitLiveChat = () => {
  widgetConversation.supportWizard.submitSelectedConnectOption({ connectOption: ConnectOptionType.LIVE_CHAT });
  widgetConversation.supportWizard.closeThresholdLimitModal();
};

const handleThresholdLimitCall = () => {
  widgetConversation.supportWizard.submitSelectedConnectOption({ connectOption: ConnectOptionType.VOICE_CALL });
  widgetConversation.supportWizard.closeThresholdLimitModal();
};
</script>

<style lang="scss" scoped>
.chat-widget {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
}

.chat-input-wrapper {
  position: relative;
  z-index: 3;
  cursor: pointer;
}

.chat-overlay-enter-from,
.chat-overlay-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.chat-overlay-enter-to,
.chat-overlay-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
